import { useState } from "react";
import {
  useUpdateConstructionProject,
  useUpdateConstructionSite,
} from "../../api";

export interface UpdateApiResponse {
  updatedValue: { key: string; value: string };
  completed: boolean;
  status: number;
}

export const useUpdateConstructionProjectInformation = (
  constructionProjectId: string | undefined,
  onSave: (refreshTimeStamp: string) => void
) => {
  const [updateResult, setUpdateResult] = useState<UpdateApiResponse>({
    updatedValue: { key: "", value: "" },
    completed: true,
    status: 0,
  });

  const update = useUpdateConstructionProject(constructionProjectId || "");

  const updateProjectName = async (updatedName: string) => {
    const projectUpdatedName = {
      name: updatedName,
      address: {},
      sites: {},
    };

    setUpdateResult({
      updatedValue: { key: "projectName", value: updatedName },
      completed: false,
      status: 0,
    });

    const result = await update(projectUpdatedName);
    if (result.error) {
      setUpdateResult({
        updatedValue: { key: "projectName", value: "" },
        completed: true,
        status: 400,
      });
    } else {
      setUpdateResult({
        updatedValue: { key: "projectName", value: updatedName },
        completed: true,
        status: 200,
      });

      onSave(new Date().toISOString());
    }
  };

  const updateProjectCity = async (updatedCity: string) => {
    const projectUpdatedCity = {
      name: {},
      address: {
        postalCode: "",
        city: updatedCity,
        street: "",
      },
      sites: {},
    };

    setUpdateResult({
      updatedValue: { key: "projectLocation", value: updatedCity },
      completed: false,
      status: 0,
    });

    const result = await update(projectUpdatedCity);

    if (result.error) {
      setUpdateResult({
        updatedValue: { key: "projectLocation", value: "" },
        completed: true,
        status: 400,
      });
    } else {
      setUpdateResult({
        updatedValue: { key: "projectLocation", value: updatedCity },
        completed: true,
        status: 200,
      });

      onSave(new Date().toISOString());
    }
  };

  const resetUpdateInfo = () => {
    setUpdateResult({
      updatedValue: { key: "", value: "" },
      completed: true,
      status: 0,
    });
  };

  return {
    updateResult,
    updateProjectName,
    resetUpdateInfo,
    updateProjectCity,
  };
};

export const useUpdateConstructionSiteInformation = (
  constructionSiteId: string | undefined,
  onSave: (refreshTimeStamp: string) => void
) => {
  const [updateResult, setUpdateResult] = useState<UpdateApiResponse>({
    updatedValue: { key: "", value: "" },
    completed: true,
    status: 0,
  });

  const update = useUpdateConstructionSite(constructionSiteId || "");

  const updateSiteName = async (updatedName: string) => {
    const siteUpdatedName = {
      name: updatedName,
      address: {},
      sites: {},
    };

    setUpdateResult({
      updatedValue: { key: "siteName", value: updatedName },
      completed: false,
      status: 0,
    });

    const result = await update(siteUpdatedName);
    if (result.error) {
      setUpdateResult({
        updatedValue: { key: "siteName", value: "" },
        completed: true,
        status: 400,
      });
    } else {
      setUpdateResult({
        updatedValue: { key: "siteName", value: updatedName },
        completed: true,
        status: 200,
      });

      onSave(new Date().toISOString());
    }
  };

  const resetUpdateInfo = () => {
    setUpdateResult({
      updatedValue: { key: "", value: "" },
      completed: true,
      status: 0,
    });
  };

  return {
    updateResult,
    updateSiteName,
    resetUpdateInfo,
  };
};
